import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchCurrentUser, login as loginRequest, logout as logoutRequest } from '@/api/auth';
import { flushOfflineMutations, setSessionExpiredHandler } from '@/api/client';
import { broadcastLogout, subscribeToLogout } from '@/auth/authBroadcast';
import { getSelectedPropertyId, setSelectedPropertyId } from '@/auth/selectedPropertyStore';
import { clearAccessToken, setAccessToken } from '@/auth/tokenStore';
import type { AuthenticatedUser } from '@/api/types';
import { setOfflineCacheUserScope } from '@/offline/offlineApiCache';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  /** True only while the initial silent-refresh-on-load check is running. */
  isInitializing: boolean;
  /** Resolves with the freshly-authenticated user so callers (e.g.
   * LoginPage) can route on `onboarding_step` without waiting for a
   * re-render. */
  login: (email: string, password: string) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  /** Re-syncs the cached user from /auth/me — e.g. after a profile edit
   * or once a property has been created — so the shell (name, nav,
   * onboarding_step) reflects the change without a full reload. */
  refreshUser: () => Promise<void>;
  selectedPropertyId?: number | null;
  selectProperty?: (propertyId: number) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const clearSession = useCallback(() => {
    clearAccessToken();
    setOfflineCacheUserScope(null);
    setSelectedPropertyId(null);
    setUser(null);
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(clearSession);
    return () => setSessionExpiredHandler(null);
  }, [clearSession]);

  useEffect(() => subscribeToLogout(clearSession), [clearSession]);

  // On first load (e.g. a page refresh), there is no access token in
  // memory yet, but a still-valid refresh-token cookie may exist from a
  // previous visit. fetchCurrentUser() will 401, which triggers the
  // client's automatic refresh-and-retry (see src/api/client.ts) — if
  // that succeeds, the user is silently re-authenticated without
  // re-entering credentials.
  useEffect(() => {
    let cancelled = false;

    fetchCurrentUser()
      .then((envelope) => {
        if (!cancelled) {
          syncSelectedProperty(envelope.data);
          setOfflineCacheUserScope(envelope.data.id);
          setUser(envelope.data);
        }
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsInitializing(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const envelope = await loginRequest(email, password);
    setAccessToken(envelope.data.access_token);
    // /auth/login's own response is already the same userPayload() shape
    // as /auth/me (see App\Http\Controllers\Api\V1\AuthController), so
    // this doesn't strictly need a follow-up /auth/me call — but issuing
    // one anyway keeps this resilient to that ever drifting apart again
    // (it did once before), at the cost of one extra request per login.
    const me = await fetchCurrentUser();
    syncSelectedProperty(me.data);
    setOfflineCacheUserScope(me.data.id);
    setUser(me.data);
    return me.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      clearSession();
      broadcastLogout();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const me = await fetchCurrentUser();
    syncSelectedProperty(me.data);
    setOfflineCacheUserScope(me.data.id);
    setUser(me.data);
  }, []);

  const selectProperty = useCallback(async (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    const me = await fetchCurrentUser();
    setOfflineCacheUserScope(me.data.id);
    setUser(me.data);
  }, []);

  useEffect(() => {
    function flushWhenOnline() {
      void flushOfflineMutations();
    }

    window.addEventListener('online', flushWhenOnline);
    flushWhenOnline();

    return () => window.removeEventListener('online', flushWhenOnline);
  }, []);

  const hasPermission = useCallback(
    (permission: string) => user?.permissions?.includes(permission) ?? false,
    [user]
  );

  const hasRole = useCallback((role: string) => user?.roles.includes(role) ?? false, [user]);

  const value = useMemo(
    () => ({
      user,
      isInitializing,
      login,
      logout,
      refreshUser,
      selectedPropertyId: getSelectedPropertyId(),
      selectProperty,
      hasPermission,
      hasRole,
    }),
    [user, isInitializing, login, logout, refreshUser, selectProperty, hasPermission, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function syncSelectedProperty(user: AuthenticatedUser) {
  const storedPropertyId = getSelectedPropertyId();
  const hasStoredProperty = storedPropertyId !== null
    && user.memberships.some((membership) => membership.property_id === storedPropertyId);

  if (!hasStoredProperty) {
    setSelectedPropertyId(user.property?.id ?? null);
  }
}
